import React, { type ComponentProps } from 'react';
import {
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  type ListBoxProps as AriaListBoxProps,
  Collection,
  Header,
  type ListBoxItemProps as AriaListBoxItemProps,
  ListBoxSection,
  type SectionProps,
  composeRenderProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';

import { CheckSVG } from 'components/iconsSVG/check';
import { getDomainValue } from 'utils/domain';
import { LabTickIcon } from 'components/camperlab/icons/tick';

function Tick(props: ComponentProps<'svg'>) {
  return getDomainValue({
    camper: <CheckSVG {...props} />,
    nnormal: <CheckSVG {...props} />,
    camperlab: <LabTickIcon {...props} />,
  });
}

type ListBoxProps<T> = Omit<AriaListBoxProps<T>, 'layout' | 'orientation'> & {
  variant?: 'default' | 'empty';
};

const listBoxStyles = tv({
  base: 'border outline-0',
  variants: {
    variant: {
      default: 'border-dark p-1',
      empty: 'border-transparent',
    },
  },
});

export function ListBox<T extends object>({ children, variant = 'default', ...props }: ListBoxProps<T>) {
  return (
    <AriaListBox {...props} className={composeRenderProps(props.className, (className, renderProps) => listBoxStyles({ variant, className, ...renderProps }))}>
      {children}
    </AriaListBox>
  );
}

export const itemStyles = tv({
  // extend: focusRing,
  base: 'group relative flex cursor-default select-none items-center gap-8 text-sm will-change-transform forced-color-adjust-none lg:text-xs',
  variants: {
    variant: {
      default:
        'px-2.5 py-1.5 -outline-offset-2 hover:bg-subtle selected:bg-neutral-inverted selected:text-inverted selected:-outline-offset-4 disabled:text-disabled selected:forced-colors:bg-[Highlight] selected:forced-colors:text-[HighlightText] selected:forced-colors:outline-[HighlightText] disabled:forced-colors:text-[GrayText]',
      empty: 'cursor-pointer justify-between py-0.5 pr-1 outline-none hover:underline focus:underline',
    },
  },
});

type ListBoxItemProps = AriaListBoxItemProps & {
  variant?: 'default' | 'empty';
};

export function ListBoxItem({ variant = 'default', ...props }: ListBoxItemProps) {
  const textValue = props.textValue || (typeof props.children === 'string' ? props.children : undefined);
  return (
    <AriaListBoxItem
      {...props}
      textValue={textValue}
      className={composeRenderProps(props.className, (className, renderProps) => itemStyles({ variant, className, ...renderProps }))}
    >
      {composeRenderProps(props.children, (children) => (
        <>
          {children}

          <div className="absolute bottom-0 left-4 right-4 hidden h-px bg-neutral/20 forced-colors:bg-[HighlightText] [.group[data-selected]:has(+[data-selected])_&]:block" />
        </>
      ))}
    </AriaListBoxItem>
  );
}

export const dropdownItemStyles = tv({
  base: 'group flex cursor-pointer select-none items-center gap-4 p-1.5 outline outline-0 forced-color-adjust-none lab:text-sm md:py-1 lab:lg:text-xs',
  variants: {
    isDisabled: {
      false: '',
      true: 'text-disabled forced-colors:text-[GrayText]',
    },
    isSelected: {
      true: 'bg-neutral-inverted !text-inverted lab:bg-clear lab:!text-copy forced-colors:bg-[Highlight] forced-colors:text-[HighlightText]',
    },
    isFocused: {
      true: 'bg-subtle selected:bg-neutral-inverted lab:bg-clear lab:!text-copy forced-colors:bg-[Highlight] forced-colors:text-[HighlightText]',
    },
  },
  compoundVariants: [
    {
      isFocused: false,
      isOpen: true,
      className: 'bg-subtle',
    },
  ],
});

export function DropdownItem(props: ListBoxItemProps) {
  const textValue = props.textValue || (typeof props.children === 'string' ? props.children : undefined);
  return (
    <AriaListBoxItem {...props} textValue={textValue} className={dropdownItemStyles}>
      {composeRenderProps(props.children, (children, { isSelected, isFocused }) => (
        <>
          <span className="flex flex-1 items-center gap-2 truncate lab:font-bold">{children}</span>
          <span className="flex items-center justify-end empty:hidden">
            <Tick
              data-selected={getDomainValue({ camper: isSelected, nnormal: isSelected, camperlab: isSelected || isFocused })}
              className="mr-1 size-3.5 data-[selected=false]:text-transparent"
            />
          </span>
        </>
      ))}
    </AriaListBoxItem>
  );
}

export interface DropdownSectionProps<T> extends SectionProps<T> {
  title?: string;
}

export function DropdownSection<T extends object>(props: DropdownSectionProps<T>) {
  return (
    <ListBoxSection className="after:block after:h-[5px] after:content-['']">
      <Header className="sticky top-0 z-10 truncate border-b px-4 py-1 text-xs font-semibold uppercase text-copy supports-[-moz-appearance:none]:bg-subtle [&+*]:mt-1">
        {props.title}
      </Header>
      <Collection items={props.items}>{props.children}</Collection>
    </ListBoxSection>
  );
}
